import {
  Box,
  Button,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/app-button/AppButton";
import AppDialogDelete from "../../components/app-dialog-delete/AppDialogDelete";
import AppTable from "../../components/app-table/AppTable";
import Iconify from "../../components/iconify/Iconify";
import {
  attributeAction,
  attributeDelete,
  getAttributes,
} from "../../reduxStore/slices/attributeSlice";
import { useNotify } from "../../utils/snackbarUtils";
import AppDialog from "../../components/app-dialog/AppDialog";

function AttributesList(props) {
  const tableHead = [
    { id: "name", label: "Attribute Name", align: "left" },
    {
      id: "attributeValueCount",
      label: "Attribute Values Count",
      align: "center",
    },
    { id: "status", label: "Status", align: "center" },
    { id: "createdAt", label: "Created At", align: "left" },
    { id: "actions", label: "Actions", align: "center", width: "140px" },
  ];

  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { list, loading: isLoading } = useSelector((state) => state.attribute);

  const storeId = user?.storeId;
  const tableRows = list;

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState([]);

  const getAllAttributes = async () => {
    try {
      await dispatch(getAttributes({ storeId })).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  // Load attributes
  useEffect(() => {
    if (storeId) {
      getAllAttributes();
    }
  }, [storeId]);

  const handleDialogCancel = () => {
    setIsViewDialogOpen(false);
  };

  const handleAddAttribute = () => {
    navigate("/app/config/attribute-value/add");
  };

  const handleDeleteClose = () => {
    setIsDeleteDialogOpen(false);
  };

  const toggleStatus = async (event, row) => {
    const obj = {
      store_id: storeId,
      uid: row.uid,
      id: row.id,
      record_status: event.target.value === "A" ? "D" : "A",
    };
    try {
      const res = await dispatch(attributeAction(obj)).unwrap();
      showSuccess(res.message);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleteDialogOpen(false);
    const obj = {
      store_id: storeId,
      uid: selectedRow.uid,
      id: selectedRow.id,
      record_status: "R",
    };
    try {
      const res = await dispatch(attributeDelete(obj)).unwrap();
      showSuccess(res.message);
    } catch (err) {
      handleApiError(err);
    }
  };

  const editActionClick = (e, row) => {
    setSelectedRow(row);
    navigate(`/app/config/attribute-value/edit/${row.uid}`);
  };
  const viewActionClick = (e, row) => {
    setIsViewDialogOpen(true);
    setViewRecord(row || {});
  };

  const deleteActionClick = async (event, row) => {
    setIsDeleteDialogOpen(true);
    setSelectedRow(row);
  };

  const cellClick = async (field_name, event, row) => {
    setIsViewDialogOpen(true);
    setViewRecord(row || {});
  };

  return (
    <Box
      sx={{
        margin: "25px 60px",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Tooltip title="Back to Configuration">
            <IconButton onClick={() => navigate("/app/config")}>
              <Iconify icon="eva:arrow-back-fill" width={24} height={24} />
            </IconButton>
          </Tooltip>

          <Typography variant="h5" gutterBottom>
            Attributes & Values
          </Typography>
        </Stack>
        <Link style={{ textDecoration: "none" }}>
          {tableRows.length > 0 && !isLoading && (
            <AppButton
              onClick={handleAddAttribute}
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
            >
              New Attribute
            </AppButton>
          )}
        </Link>
      </Stack>
      <Box>
        <AppTable
          tableHead={tableHead}
          tableRows={tableRows}
          listType="attributeTables"
          searchPlaceholder="Search By Attribute Name"
          toggleSwitchStatus={toggleStatus}
          editActionClick={editActionClick}
          viewActionClick={viewActionClick}
          deleteActionClick={deleteActionClick}
          searchField={"name"}
          cellClick={cellClick}
          handleAddAction={handleAddAttribute}
          emptyTitle="No attributes available"
          emptyDesc="Add new attribute to get started."
          emptyButtonLabel="New Attribute"
          isLoading={isLoading}
        />
      </Box>

      {isViewDialogOpen && (
        <AppDialog
          maxWidth="xs"
          isOpen={isViewDialogOpen}
          id={"view_attr_values_dialog"}
          title={`View Attribute`}
          footer={
            <Box
              style={{
                display: "flex",
                gap: 15,
              }}
            >
              <Button size="medium" onClick={handleDialogCancel}>
                Close
              </Button>
            </Box>
          }
        >
          <Box>
            <Box
              sx={{
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Attribute Name
              </Typography>
              <Typography variant="body1">{viewRecord?.name}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body1">{viewRecord?.description}</Typography>
            </Box>

            <Stack
              spacing={3}
              style={{
                marginTop: 30,
                marginBottom: 30,
              }}
            >
              <Box>
                <Box className="dine-area-table-header">
                  <Box>Attribute Values</Box>
                  <Box></Box>
                  {/* <Box className='dine-area-no-action-cell'>Action</Box> */}
                </Box>
                <Box className="dine-area-table-body">
                  {viewRecord &&
                    viewRecord.attributeValues?.map((tbl, key) => {
                      return (
                        <Box key={tbl.uid} className="dine-area-table-row">
                          <Box>{tbl.value}</Box>
                        </Box>
                      );
                    })}
                </Box>
              </Box>
            </Stack>
          </Box>
        </AppDialog>
      )}

      {isDeleteDialogOpen && (
        <AppDialogDelete
          isOpen={isDeleteDialogOpen}
          id={"delete_attribute_dialog"}
          title={<>Delete Attribute</>}
          footer={
            <>
              <Button
                autoFocus
                onClick={handleDeleteConfirm}
                className="confirm-delete-btn"
              >
                Delete
              </Button>
              <Button
                onClick={handleDeleteClose}
                variant="outlined"
                className="cancel-delete-btn"
              >
                Cancel
              </Button>
            </>
          }
        >
          <Typography
            style={{
              fontSize: 14,
              color: "#000",
            }}
          >
            Are you sure want to delete attribute?
          </Typography>
        </AppDialogDelete>
      )}
    </Box>
  );
}

export default AttributesList;
